// src/components/careers/JobModal.tsx
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { JobList } from "./JobList";
import { ApplyForm } from "./ApplyForm";
import { SuccessPanel } from "./SuccessPanel";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import type { Job } from "@/types/job";
import type { ApplicationForm } from "@/types/application";

interface JobModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobs: Job[];
  loading: boolean;
  error: string | null;
  selectedJob: Job | null;
  isApplying: boolean;
  applied: boolean;
  duplicate: boolean;
  formData: ApplicationForm;
  setFormData: React.Dispatch<React.SetStateAction<ApplicationForm>>;
  onApply: (job: Job) => void;
  onBack: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onFileDrop: (e: React.DragEvent) => void;
  onFileSelect: (file: File | null) => void;
  removeFile: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  fetchJobs: () => void;
  resetModal: () => void;
}

export function JobModal({
  open,
  onOpenChange,
  jobs,
  loading,
  error,
  selectedJob,
  isApplying,
  applied,
  duplicate,
  formData,
  setFormData,
  onApply,
  onBack,
  onSubmit,
  onFileDrop,
  onFileSelect,
  removeFile,
  fileInputRef,
  fetchJobs,
  resetModal,
}: JobModalProps) {
  const { t } = useTranslation();

  const handleApplyAgain = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      linkedin: "",
      coverLetter: "",
      jobId: "",
      agreeTerms: false,
      resumeFile: null,
      resumeFileName: "",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
    fetchJobs();
    resetModal();
  };

  const handleBackToJobs = () => {
    resetModal();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="text-2xl font-bold">
            {isApplying
              ? t("careers.jobModal.applyFor", { jobTitle: selectedJob?.title })
              : t("careers.jobModal.openPositions")}
          </DialogTitle>
          <DialogDescription>
            {isApplying
              ? t("careers.jobModal.uploadResume")
              : t("careers.jobModal.hiring")}
          </DialogDescription>
        </DialogHeader>

        <div className="p-6">
          {/* 1. JOB LIST */}
          {!isApplying ? (
            <JobList
              jobs={jobs}
              loading={loading}
              error={error}
              onApply={onApply}
              onClose={() => onOpenChange(false)}
            />
          ) : applied ? (
            /* 2. SUCCESS PANEL */
            <SuccessPanel
              onApplyAgain={handleApplyAgain}
              fileInputRef={fileInputRef}
              fetchJobs={fetchJobs}
            />
          ) : duplicate ? (
            /* 3. ALREADY APPLIED PANEL */
            <div className="text-center py-8">
              <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <svg
                  className="h-8 w-8 text-amber-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {t("careers.jobModal.alreadyApplied.title")}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t("careers.jobModal.alreadyApplied.message")}
              </p>
              <Button onClick={handleBackToJobs} variant="outline">
                {t("careers.jobModal.backToJobs")}
              </Button>
            </div>
          ) : (
            /* 4. APPLY FORM */
            <ApplyForm
              jobTitle={selectedJob?.title ?? ""}
              formData={formData}
              setFormData={setFormData}
              onSubmit={onSubmit}
              onBack={onBack}
              onFileDrop={onFileDrop}
              onFileSelect={onFileSelect}
              removeFile={removeFile}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}